import {
  AlreadyCanceledReservationErrorResponse,
  ReservationReceiptNotReady,
  ReservationUnavailableErrorResponse,
  ProcessingPaymentErrorResponse,
  ReserveErrorResponse,
  ServerErrorResponse,
  UnauthorizedErrorResponse,
  UnfinishedUserProfileErrorResponse,
  UpdateUserErrorResponse,
  CardErrorResponse,
  LegacyCardErrorResponse,
  ActionRequiredResponse,
  ReservationResourceNotFound,
} from './types.ts';
import {
  extractEmailDomainSuggestion,
  type EmailDomainSuggestion,
} from './emailDomainSuggestion.ts';

type Responses =
  | ReserveErrorResponse
  | UpdateUserErrorResponse
  | ReservationUnavailableErrorResponse
  | ProcessingPaymentErrorResponse
  | ActionRequiredResponse
  | ServerErrorResponse
  | UnauthorizedErrorResponse
  | UnfinishedUserProfileErrorResponse
  | AlreadyCanceledReservationErrorResponse
  | ReservationReceiptNotReady
  | CardErrorResponse
  | LegacyCardErrorResponse
  | ReservationResourceNotFound;

type ResponseNames =
  | 'ReserveErrorResponse'
  | 'UpdateUserErrorResponse'
  | 'ReservationUnavailableErrorResponse'
  | 'ProcessingPaymentErrorResponse'
  | 'ServerErrorResponse'
  | 'ActionRequiredResponse'
  | 'UnauthorizedErrorResponse'
  | 'UnfinishedUserProfileErrorResponse'
  | 'AlreadyCanceledReservationErrorResponse'
  | 'ReservationReceiptNotReady'
  | 'CardErrorResponse'
  | 'ReservationResourceNotFound';

interface ApiErrorParams {
  url: URL;
  response: Responses;
}

export class ApiError extends Error {
  url: URL;
  response: Responses;
  type: ResponseNames | 'Error';
  /**
   * Did-you-mean suggestion for an `email_domain` rejection [GRO-6465].
   * Only ever populated for `UpdateUserErrorResponse`; `null` otherwise,
   * including when the payload carries a malformed `email_domain` key.
   */
  emailDomainSuggestion: EmailDomainSuggestion | null = null;

  constructor(params: ApiErrorParams) {
    super();
    this.url = params.url;
    this.response = params.response;

    this.type = ApiError.match(this.response) || 'Error';

    switch (this.type) {
      case 'ServerErrorResponse':
        this.message = (this.response as ServerErrorResponse).message;
        break;

      case 'ReservationReceiptNotReady':
        this.message = (this.response as ReservationReceiptNotReady).message;
        break;

      case 'AlreadyCanceledReservationErrorResponse':
        this.message = (this.response as AlreadyCanceledReservationErrorResponse).message;
        break;

      case 'UnfinishedUserProfileErrorResponse':
        this.message = (this.response as UnfinishedUserProfileErrorResponse).message;
        break;

      case 'UnauthorizedErrorResponse':
        this.message = (this.response as UnauthorizedErrorResponse).message;
        break;

      case 'ReserveErrorResponse':
        this.message = (this.response as ReserveErrorResponse).message;
        break;

      case 'UpdateUserErrorResponse':
        this.message = (this.response as UpdateUserErrorResponse).errors.email[0];
        this.emailDomainSuggestion = extractEmailDomainSuggestion(this.response);
        break;

      case 'ReservationUnavailableErrorResponse':
        this.message = (this.response as ReservationUnavailableErrorResponse).message;
        break;

      case 'ProcessingPaymentErrorResponse':
        this.message = (this.response as ProcessingPaymentErrorResponse).message;
        break;

      case 'CardErrorResponse':
        this.message = (this.response as CardErrorResponse | LegacyCardErrorResponse).message;
        break;
      case 'ActionRequiredResponse':
        break;
    }
  }

  static match = <ResponseT>(
    response: ReserveErrorResponse | UpdateUserErrorResponse | ResponseT
  ): ResponseNames | false => {
    const type = (response as ReserveErrorResponse).type ?? undefined;

    const message =
      (
        response as
          | ReservationUnavailableErrorResponse
          | ProcessingPaymentErrorResponse
          | ServerErrorResponse
          | UnauthorizedErrorResponse
          | UnfinishedUserProfileErrorResponse
          | AlreadyCanceledReservationErrorResponse
          | ReservationReceiptNotReady
          | ActionRequiredResponse
          | CardErrorResponse
      ).message ?? undefined;
    const email = (response as UpdateUserErrorResponse).errors?.email ?? [];

    switch (true) {
      // Must precede the generic `!!type` arm so card failures classify as card errors
      case type === 'card_error':
        return 'CardErrorResponse';

      case !!type:
        return 'ReserveErrorResponse';

      case message === 'Please tell us a little about yourself.':
        return 'UnfinishedUserProfileErrorResponse';

      case message === "Reservation doesn't have an order created":
        return 'ReservationReceiptNotReady';

      case message === 'Reservation has been cancelled.':
        return 'AlreadyCanceledReservationErrorResponse';

      case message === 'Reservation has already been canceled':
        return 'AlreadyCanceledReservationErrorResponse';

      case message === 'This reservation is no longer available.':
        return 'ReservationUnavailableErrorResponse';

      case message === 'Reservation is sold out.':
        return 'ReservationUnavailableErrorResponse';

      case message === 'Reservation has been claimed.':
        return 'ReservationUnavailableErrorResponse';

      // Claim-guard terminal states: dead links route to the expired screen
      // instead of being silently swallowed [HOS-2030]
      case message === 'Cannot claim a cancelled or failed reservation.':
        return 'ReservationUnavailableErrorResponse';

      case message === 'Reservation has expired.':
        return 'ReservationUnavailableErrorResponse';

      case message === 'Reservation payment in progress.':
        return 'ProcessingPaymentErrorResponse';

      // The locked claim guard's variant of the same state — a double-clicked
      // retry's second request lands here [HOS-2030]
      case message === 'Reservation is already claimed; payment may be in progress.':
        return 'ProcessingPaymentErrorResponse';

      case message === 'This action is unauthorized.':
        return 'UnauthorizedErrorResponse';

      case message === 'Payment processing requires action.':
        return 'ActionRequiredResponse';

      case message === 'Server Error':
        return 'ServerErrorResponse';

      // Deploy-skew fallback for the pre-HOS-2029 API (LegacyCardErrorResponse):
      // same failure, no type discriminator. Delete this arm (and the legacy
      // union member) once every API environment emits type: 'card_error'.
      case message ===
        'There was an error processing your payment. Please verify your card information or use another card.':
        return 'CardErrorResponse';

      case email.length > 0:
        return 'UpdateUserErrorResponse';
    }

    return false;
  };
}
